import React, { useState, useEffect } from 'react';
import { Table, Container, Button, Modal, Form } from 'react-bootstrap';
import axios from 'axios';

const ProductOverviewIcons = () => {
  const [icons, setIcons] = useState([]);
  const [products, setProducts] = useState([]); // To store fetched products for the dropdown
  const [imagePath, setImagePath] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editIconId, setEditIconId] = useState(null);
  const [formData, setFormData] = useState({ p_id: '', title: '', image: null });

  // Fetch icons from the API
  const fetchIcons = async () => {
    try {
      const response = await axios.get('https://api.biznetusa.com/api/get-productoverviewhomeicons');
      if (response.data.overview_home_section_icons) {
        setIcons(response.data.overview_home_section_icons);
        setImagePath(response.data.imagePath); // Get the image path from the response
      } else {
        console.error('Error: No icons found in the response.');
      }
    } catch (error) {
      console.error('Error fetching icons:', error);
    }
  };

  // Fetch products from the API to populate the dropdown
  const fetchProducts = async () => {
    try {
      const response = await axios.get('https://api.biznetusa.com/api/get-products');
      if (response.data.products) {
        setProducts(response.data.products); // Set products for the dropdown
      } else {
        console.error('Error: No products found in the response.');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  useEffect(() => {
    fetchIcons();
    fetchProducts(); // Fetch products when the component mounts
  }, []);

  // Handle input change for the form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleImageChange = (e) => {
    setFormData((prevData) => ({ ...prevData, image: e.target.files[0] }));
  };

  // Handle form submission to add a new icon
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    formDataToSend.append('p_id', formData.p_id);
    formDataToSend.append('title', formData.title);
    formDataToSend.append('image', formData.image);

    try {
      await axios.post('https://api.biznetusa.com/api/store-productoverviewhomeicon', formDataToSend);
      alert('Icon added successfully!');
      setShowAddModal(false);
      fetchIcons(); // Refresh the icon list
    } catch (error) {
      console.error('Error adding icon:', error);
    }
  };

  // Open edit modal and set current icon data
  const handleEdit = (icon) => {
    setEditIconId(icon.id);
    setFormData({ p_id: icon.p_id, title: icon.title, image: null });
    setShowEditModal(true);
  };

  // Handle form submission to update an icon
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    formDataToSend.append('p_id', formData.p_id);
    formDataToSend.append('title', formData.title);
    if (formData.image) {
      formDataToSend.append('image', formData.image);
    }

    try {
      await axios.post(`https://api.biznetusa.com/api/update-productoverviewhomeicon/${editIconId}`, formDataToSend);
      alert('Icon updated successfully!');
      setShowEditModal(false);
      fetchIcons(); // Refresh the icon list
    } catch (error) {
      console.error('Error updating icon:', error);
    }
  };

  // Handle deleting an icon
  const handleDelete = async (id) => {
    try {
      await axios.delete(`https://api.biznetusa.com/api/deleteproductoverviewhomeicon/${id}`);
      alert('Icon deleted successfully!');
      fetchIcons(); // Refresh the icon list
    } catch (error) {
      console.error('Error deleting icon:', error);
    }
  };

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Product Overview Icons</h2>

      {/* Add Icon Button */}
      <Button variant="primary" className="mb-3" onClick={() => setShowAddModal(true)}>
        Add New Icon
      </Button>

      {/* Table for displaying icons */}
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Product ID</th>
            <th>Title</th>
            <th>Icon</th>
            <th>Created At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {icons.map((icon) => (
            <tr key={icon.id}>
              <td>{icon.id}</td>
              <td>{icon.p_id}</td>
              <td>{icon.title}</td>
              <td>
                <img
                  src={`${imagePath}${icon.icon}`}
                  alt={icon.title}
                  style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                />
              </td>
              <td>{new Date(icon.created_at).toLocaleDateString()}</td>
              <td>
                <Button variant="warning" onClick={() => handleEdit(icon)}>
                  Edit
                </Button>{' '}
                <Button variant="danger" onClick={() => handleDelete(icon.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Add Icon Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Icon</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSubmit}>
            <Form.Group controlId="formProductId">
              <Form.Label>Select Product</Form.Label>
              <Form.Control
                as="select"
                name="p_id"
                value={formData.p_id}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name || product.title} (ID: {product.id})
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="formTitle">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group controlId="formImage">
              <Form.Label>Image</Form.Label>
              <Form.Control type="file" name="image" onChange={handleImageChange} required />
            </Form.Group>
            <Button variant="primary" type="submit">
              Add Icon
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Icon Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Icon</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditSubmit}>
            <Form.Group controlId="formProductIdEdit">
              <Form.Label>Select Product</Form.Label>
              <Form.Control
                as="select"
                name="p_id"
                value={formData.p_id}
                onChange={handleInputChange}
                required
              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name || product.title} (ID: {product.id})
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="formTitleEdit">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Form.Group controlId="formImageEdit">
              <Form.Label>Image (optional)</Form.Label>
              <Form.Control type="file" name="image" onChange={handleImageChange} />
            </Form.Group>
            <Button variant="primary" type="submit">
              Update Icon
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ProductOverviewIcons;
